import React, { useEffect, useState, useRef } from 'react';
import { useCookies } from 'react-cookie';
import { useHistory, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import MainController from 'components/modules/Music/mainController';
import Detect from 'components/modules/Detect';
import Visualizer from 'components/modules/Visualizer';
import Hints from 'components/modules/Hints';
import Download from 'components/modules/Download';
import Recorder from 'components/modules/Recorder';
import Controls from 'components/modules/Controls';
import { CookieRedirect, ArrayAvg } from 'components/utils/helpers';

// Icons
import Logo from 'svg/logo';
import Question from 'svg/question';
import Fx from 'svg/fx';
import Note from 'svg/note';
import Record from 'svg/record';


const Main = (props) => {
  // Redirect if user is too young
  CookieRedirect();
  // eslint-disable-next-line
  const location = useLocation();
  // History
  const history = useHistory();
  // Cookies
  const [cookie, removeCookie] = useCookies(['hasSeenIntro']);
  // State to save
  const [warning, setWarning] = useState(false);
  const [hasShownWarning, setHasShownWarning] = useState(false);
  const [recording, setRecording] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [currentMeasure, setCurrentMeasure] = useState(0);
  const [showViz, setShowViz] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [controller, setController] = useState(null);
  const [hasLiveBox, setHasLiveBox] = useState(false);
  const [hasLockedBox, setHasLockedBox] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [hasPreviouslyShownOverlay, setHasPreviouslyShownOverlay] = useState(false);
  const [degrade, setDegrade] = useState(false);
  // Refs
  const controllerRef = useRef(null);
  const warningCheck = useRef(true);
  const lockState = useRef('unlocked');
  const lockedBoxes = useRef([false, false, false]);
  const liveBox = useRef({});
  // Variables
  const minFPS = 20;
  const frameCheckNum = 20;

  /**
   * Toggle the visualizer
   */
  const toggleViz = () => {
    if (!showViz) {
      setShowViz(true);
    } else {
      setShowViz(false);
    }
  };

  /**
   * Toggle the hints
   */
  const toggleHints = () => {
    if (!showHints) {
      setIsPlaying(false);
      controller.stopTransport();
      setShowHints(true);
    } else {
      setShowHints(false);
    }
  };

  /**
   * Start recording
   */
  const record = () => {
    setIsRecording(true);
    lockState.current = 'locked';
    setIsLocked(true);
    lockedBoxes.current[liveBox.current.idNum] = liveBox.current;
  };

  /**
   * Toggle download screen
   */
  const toggleDownload = () => {
    if (!showDownload) {
      setShowDownload(true);
    } else {
      setShowDownload(false);
    }
  };

  /**
   * Toggle the controls
   */
  const toggleControls = () => {
    if (!showControls) {
      setShowControls(true);
    } else {
      setShowControls(false);
    }
  };

  /**
   * Setup the main music controller
   */
  useEffect(() => {
    const callback = (step, notesAtStep) => {
      setCurrentMeasure(step);
    }
    // Instantiate the main music controller, and set it
    controllerRef.current = new MainController(callback);
    setController(controllerRef.current);
    Object.freeze(controller);
    // eslint-disable-next-line
  }, []);

  /**
   * Monitor the FPS,
   * and serve up a warning if app runs super slow
   */
  useEffect(() => {
    // Monitor the FPS
    // and serve up warning if very very slow
    // (i.e. 20 frames in a row average out to less than 20fps)
    let lastLoop = Date.now();
    let frames = 0;
    let fpsArr = [];
    function gameLoop() {
      if (warningCheck.current) {
        frames++;
        let thisLoop = Date.now();
        if (thisLoop >= lastLoop + 1000) {
          let fps = ( frames * 1000 ) / ( thisLoop - lastLoop );
          lastLoop = thisLoop;
          frames = 0;
          fpsArr.unshift(fps);
          fpsArr = fpsArr.slice(0, frameCheckNum);
          if (fpsArr.length === frameCheckNum && ArrayAvg(fpsArr) < minFPS) {
            controllerRef.current.stopTransport();
            setIsPlaying(false);
            setWarning(true);
          }
        }
        requestAnimationFrame(gameLoop);
      }
    }
    requestAnimationFrame(gameLoop);
  }, []);

  /**
   * Show the intro every time
   */
  useEffect(() => {
    // if we're coming from the intro,
    // do nothing and set the hasSeenIntro cookie to false again
    if (cookie.hasSeenIntro !== 'true') {
      history.push('/intro');
    } else {
      removeCookie('hasSeenIntro');
    } // eslint-disable-next-line
  }, []);
  return (
    <>
        <article className={classnames('Main', {
          recording: isRecording
        })}>
          {/*AR Detection, visual overlay on boxes*/}
          <Detect
            controller={controller}
            currentMeasure={currentMeasure}
            isPlaying={isPlaying}
            setIsPlaying={setIsPlaying}
            showViz={showViz}
            hasLiveBox={hasLiveBox}
            hasLockedBox={hasLockedBox}
            hasVideo={hasVideo}
            setHasVideo={setHasVideo}
            setHasLiveBox={setHasLiveBox}
            setHasLockedBox={setHasLockedBox}
            lockState={lockState}
            isLocked={isLocked}
            setIsLocked={setIsLocked}
            lockedBoxes={lockedBoxes}
            liveBox={liveBox}
            hasPreviouslyShownOverlay={hasPreviouslyShownOverlay}
            setHasPreviouslyShownOverlay={setHasPreviouslyShownOverlay}
            degrade={degrade}
            showDownload={showDownload}
          />
          {/*Box lineup overlay, when we dont have a live box OR visualizer isnt open */}
          {(!hasLiveBox && !showViz) && (
            <section className='Detect__container'>
              {!hasPreviouslyShownOverlay && (
                <>
                  <div className='Detect__pseudo-overlay'></div>
                  <div className='Detect__pseudo-overlay top'></div>
                </>
              )}
              <Logo className='Detect__logo' />
              {hasVideo && (
                <div className='Detect__notice'>
                  <h1 className='Detect__notice-text'>Line up the box!</h1>
                </div>
              )}
            </section>
          )}
          {/*Tutorial button*/}
          {!showViz && (
            <button
              title='Hints'
              className={classnames('button Question__button button button--outline', {
                show: hasLiveBox
              })}
              onClick={toggleHints}
            >
              <Question className='Question__icon Question__icon-questionmark' />
            </button>
          )}
          {/*Bottom three buttons: Visualizer, record, and adjustments*/}
          {(!showViz && !degrade) && (
            <section
              className={classnames('Adjustments', {
                show: hasLiveBox || hasLockedBox
              })}
            >
              <ul className='Adjustments__list'>
                <li className='Adjustments__list-item'>
                  <button
                    title='Sound Effects'
                    className='button Adjustments__list-button'
                    onClick={toggleViz}
                  ><Fx className='Adjustmenmts__icon Adjustments__fx-icon' /></button>
                </li>
                <li className='Adjustments__list-item'>
                  <button
                    title='Record'
                    className='button Adjustments__list-button'
                    onClick={record}
                  ><Record className='Adjustmenmts__icon Adjustments__record-icon' /></button>
                </li>
                <li className='Adjustments__list-item'>
                  <button
                    title='Sound Adjustments'
                    className='button Adjustments__list-button'
                    onClick={toggleControls}
                  ><Note className='Adjustmenmts__icon Adjustments__note-icon' /></button>
                </li>
              </ul>
            </section>
          )}
          {/*Recording overlay and logic*/}
          {isRecording && (
            <Recorder
              controller={controller}
              currentMeasure={currentMeasure}
              isPlaying={isPlaying}
              setIsPlaying={setIsPlaying}
              setIsRecording={setIsRecording}
              setRecording={setRecording}
              setShowDownload={setShowDownload}
              toggleDownload={toggleDownload}
            />
          )}
          {/*Visualizer Overlay*/}
          {showViz && (
            <Visualizer
              controller={controller}
              closeAction={toggleViz}
            />
          )}
          {/*Hints Overlay*/}
          {showHints && (
            <Hints closeAction={toggleHints} />
          )}
          {/*Download overlay*/}
          {showDownload && (
            <Download recording={recording} closeAction={toggleDownload} />
          )}
          {/*Controls Overlay*/}
          {showControls && (
            <Controls
              controller={controller}
              closeAction={toggleControls}
            />
          )}
        </article>
      {(warning && !hasShownWarning) && (
        <div className='Warning'>
          <h1 className='Warning__title'>Warning</h1>
          <div className='Warning__description'>
            <p>Looks like the experience is running slow on your device. This could mean choppy video, clipped sounds, and a generally unpleasant time. Because of this we would like to scale back some features.</p>
          </div>
          <div className='Warning__buttons'>
            <button className='button Warning__button' onClick={() => {
                setHasShownWarning(true);
                warningCheck.current = false;
                setDegrade(true);
              }}>That's OK</button>
            <button className='Warning__button' onClick={() => {
                setHasShownWarning(true);
                warningCheck.current = false;
              }}>No Thanks</button>
          </div>
        </div>
      )}
    </>
  );
}

export default Main;
